import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams, useNavigate } from "react-router-dom";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SecurityIcon from "@mui/icons-material/Security";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import { useTheme } from "@mui/material";
import { Helmet } from "react-helmet";

import {
    selectIsLoggedIn,
    selectToken,
    selectName,
    selectEmail,
    selectActiveOrganization,
} from "@/auth/authSlice";
import { GlobalStyle } from "@/themes";

/**
 * MCP OAuth Authorization Consent Screen
 *
 * This page is shown when an external MCP client (like Claude) requests
 * access to the user's MakeOS workspace. The flow:
 *
 * 1. Claude redirects user to MCP server's /authorize
 * 2. MCP server redirects here with ?auth_session=xxx&callback_url=xxx
 * 3. If user is logged in, show consent screen
 * 4. If not logged in, redirect to /login, then back here
 * 5. On "Allow", redirect to callback_url with the JWT token
 */
export default function McpAuthorize() {
    const theme = useTheme();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const isLoggedIn = useSelector(selectIsLoggedIn);
    const token = useSelector(selectToken);
    const userName = useSelector(selectName);
    const userEmail = useSelector(selectEmail);
    const activeOrg = useSelector(selectActiveOrganization);

    const [authorizing, setAuthorizing] = useState(false);
    const [error, setError] = useState("");

    const authSession = searchParams.get("auth_session");
    const callbackUrl = searchParams.get("callback_url");

    // If not logged in, save current URL and redirect to login
    useEffect(() => {
        if (!isLoggedIn) {
            localStorage.setItem(
                "savedPath",
                window.location.pathname + window.location.search,
            );
            navigate("/login");
        }
    }, [isLoggedIn, navigate]);

    const handleAuthorize = () => {
        if (!authSession || !callbackUrl || !token) {
            setError(
                "Missing authorization parameters. Please try connecting again from Claude.",
            );
            return;
        }

        setAuthorizing(true);

        // Redirect to the MCP server's callback with the token
        const separator = callbackUrl.includes("?") ? "&" : "?";
        const redirectUrl = `${callbackUrl}${separator}auth_session=${encodeURIComponent(authSession)}&token=${encodeURIComponent(token)}`;
        window.location.href = redirectUrl;
    };

    const handleDeny = () => {
        // Close the window — Claude will handle the cancellation
        window.close();
        // If window.close() doesn't work (not opened by script), go to dashboard
        navigate("/dashboard");
    };

    if (!isLoggedIn) {
        return null; // Will redirect to login via useEffect
    }

    if (!authSession || !callbackUrl) {
        return (
            <Box
                sx={{
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Helmet>
                    <title>MakeOS — Invalid Authorization Request</title>
                </Helmet>
                <GlobalStyle theme={theme.palette.mode} />
                <Paper
                    sx={{ p: 4, maxWidth: 440, width: "100%", borderRadius: 3 }}
                >
                    <Alert severity="error">
                        Invalid authorization request. Missing required
                        parameters. Please try connecting again from your MCP
                        client.
                    </Alert>
                </Paper>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            <Helmet>
                <title>MakeOS — Authorize MCP Access</title>
            </Helmet>
            <GlobalStyle theme={theme.palette.mode} />

            <Paper sx={{ p: 4, maxWidth: 440, width: "100%", borderRadius: 3 }}>
                {/* Header */}
                <Box sx={{ textAlign: "center", mb: 3 }}>
                    <img
                        src={`/MakeOS-Logo-${theme.palette.mode}-theme.svg`}
                        alt="MakeOS"
                        width={120}
                        style={{ marginBottom: 16 }}
                    />
                    <Typography variant="h5" fontWeight={700} gutterBottom>
                        Authorize Access
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        An external application is requesting access to your
                        MakeOS workspace
                    </Typography>
                </Box>

                {/* User info */}
                <Paper
                    variant="outlined"
                    sx={{
                        p: 2,
                        mb: 3,
                        borderRadius: 2,
                        backgroundColor: theme.palette.action.hover,
                    }}
                >
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                    >
                        Logged in as
                    </Typography>
                    <Typography variant="subtitle1" fontWeight={600}>
                        {userName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {userEmail}
                    </Typography>
                    {activeOrg && (
                        <Chip
                            label={activeOrg.name}
                            size="small"
                            sx={{ mt: 1 }}
                            color="primary"
                            variant="outlined"
                        />
                    )}
                </Paper>

                {/* Permissions */}
                <Paper
                    variant="outlined"
                    sx={{
                        p: 2,
                        mb: 3,
                        borderRadius: 2,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 1.5,
                        }}
                    >
                        <SecurityIcon fontSize="small" color="primary" />
                        <Typography variant="subtitle2">
                            This application will be able to:
                        </Typography>
                    </Box>
                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                        {[
                            "View and manage your projects",
                            "View and manage your bids",
                            "View and manage your clients",
                            "Query your database",
                        ].map((permission) => (
                            <Box
                                component="li"
                                key={permission}
                                sx={{ mb: 0.5 }}
                            >
                                <Typography variant="body2">
                                    {permission}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Paper>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Box sx={{ display: "flex", gap: 2 }}>
                    <Button
                        variant="outlined"
                        color="inherit"
                        fullWidth
                        onClick={handleDeny}
                        disabled={authorizing}
                    >
                        Deny
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={handleAuthorize}
                        disabled={authorizing}
                        startIcon={
                            authorizing ? (
                                <CircularProgress size={18} color="inherit" />
                            ) : (
                                <CheckCircleOutlineIcon />
                            )
                        }
                        sx={{ color: "white" }}
                    >
                        {authorizing ? "Authorizing..." : "Allow Access"}
                    </Button>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textAlign: "center", display: "block" }}
                >
                    You can revoke this access at any time from your account
                    settings.
                </Typography>
            </Paper>
        </Box>
    );
}
